import { useState } from "react";
import { buildersApi, ApiError } from "@/api";
import type { JobBuilder, JobBuilderRole, JobBuilderLinkResponse } from "@/types";
import { JOB_BUILDER_ROLES } from "@/types";
import { Button, SelectField } from "@/components/ui";
import { chatVisibilityLabel, jobBuilderRoleLabel } from "../builders/BuilderDetail";
import { FormDrawer } from "./FormDrawer";

type HistoryChoice = "keep" | "full" | "reset";

/** Edit a builder's link on this job: the role, and what the firm's app logins
 *  can see of the chat — kept as-is, opened up to the full history, or cut back
 *  to "from now". The cutoff travels as bool flags (never an int 0). */
export function JobBuilderLinkDrawer({
  jobId,
  link,
  onClose,
  onSaved,
}: {
  jobId: number;
  link: JobBuilder;
  onClose: () => void;
  onSaved: (res: JobBuilderLinkResponse) => void;
}) {
  const [role, setRole] = useState<JobBuilderRole>((link.role as JobBuilderRole) || "appointed");
  const [history, setHistory] = useState<HistoryChoice>("keep");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const res = await buildersApi.updateJobLink(jobId, link.builder_id, {
        role: role !== link.role ? role : undefined,
        show_full_history: history === "full" ? true : undefined,
        reset_history: history === "reset" ? true : undefined,
      });
      onSaved(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the builder's link.");
      setBusy(false);
    }
  }

  return (
    <FormDrawer
      title={`${link.company_name} on this job`}
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={busy}>Save</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}
      <section className="job-drawer__section">
        <h3>Role</h3>
        <SelectField label="Role on this job" value={role} onChange={(e) => setRole(e.target.value as JobBuilderRole)}>
          {JOB_BUILDER_ROLES.map((r) => (
            <option key={r} value={r}>{jobBuilderRoleLabel(r)}</option>
          ))}
        </SelectField>
      </section>
      <section className="job-drawer__section">
        <h3>Chat visibility</h3>
        <p className="job-drawer__muted">Currently: {chatVisibilityLabel(link.chat_from)}.</p>
        <label className="lead-drawer__check">
          <input type="radio" name="history" checked={history === "keep"} onChange={() => setHistory("keep")} />
          Keep as it is
        </label>
        {link.chat_from > 0 ? (
          <label className="lead-drawer__check">
            <input type="radio" name="history" checked={history === "full"} onChange={() => setHistory("full")} />
            Show the full chat history (earlier messages become visible to them)
          </label>
        ) : null}
        <label className="lead-drawer__check">
          <input type="radio" name="history" checked={history === "reset"} onChange={() => setHistory("reset")} />
          Hide everything before now
        </label>
      </section>
    </FormDrawer>
  );
}
